import React from "react";
import { ArrowRight, Building2, GraduationCap, Network } from "lucide-react";
import { HOME_SECTION_THEME_STYLES, type HomeSectionTheme } from "./homeSectionTheme";

const CHALLENGES = [
  {
    target: "Entreprises & directions formation",
    tagline: "Faire de la formation un levier de transformation et de performance.",
    description:
      "Je vous aide à structurer votre stratégie, concevoir vos dispositifs, intégrer de nouveaux usages liés à l’IA et j'accompagne vos équipes dans l’évolution durable de leurs pratiques.",
    icon: Building2,
    tone: "pink",
  },
  {
    target: "Organismes de formation et écoles",
    tagline: "Structurer et faire évoluer votre activité de formation.",
    description:
      "Je vous accompagne pour développer votre offre, faire évoluer votre organisation et vos processus, repenser vos modalités pédagogiques et intégrer les usages de l’IA pertinents pour votre activité.",
    icon: GraduationCap,
    tone: "orange",
  },
  {
    target: "Agences & acteurs du digital learning",
    tagline: "Renforcer vos projets avec une expertise complémentaire.",
    description:
      "J’interviens en conseil, ingénierie, pilotage ou production pour sécuriser un projet, renforcer une équipe, absorber une charge ou accompagner la transformation de vos cycles de production.",
    icon: Network,
    tone: "blue",
  },
] as const;

const PERSONA_MOTIF_STYLES = {
  pink: {
    outer: "bg-[#FD41A6]/[0.08]",
    ring: "border-[#FD41A6]/15 bg-[#FD41A6]/10",
    core: "border-[#FD41A6]/25 bg-[#FD41A6]/20",
    icon: "text-[#FD41A6]",
  },
  orange: {
    outer: "bg-[#FF6600]/[0.08]",
    ring: "border-[#FF6600]/15 bg-[#FF6600]/10",
    core: "border-[#FF6600]/25 bg-[#FF6600]/20",
    icon: "text-[#FF6600]",
  },
  blue: {
    outer: "bg-[#43B4E8]/[0.08]",
    ring: "border-[#43B4E8]/15 bg-[#43B4E8]/10",
    core: "border-[#43B4E8]/25 bg-[#43B4E8]/20",
    icon: "text-[#43B4E8]",
  },
} as const;

export const PersonaMotif = ({
  icon: Icon,
  tone,
  size = "default",
}: {
  icon: typeof Building2;
  tone: keyof typeof PERSONA_MOTIF_STYLES;
  size?: "default" | "compact";
}) => {
  const styles = PERSONA_MOTIF_STYLES[tone];
  const isCompact = size === "compact";

  return (
    <div
      aria-hidden="true"
      className={`relative shrink-0 overflow-hidden rounded-full ${isCompact ? "h-12 w-12" : "h-20 w-20"} ${styles.outer}`}
    >
      <span className={`absolute rounded-full border ${isCompact ? "inset-1.5" : "inset-2.5"} ${styles.ring}`} />
      <span className={`absolute rounded-full border ${isCompact ? "inset-3" : "inset-5"} ${styles.core}`} />
      <Icon
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${isCompact ? "h-5 w-5" : "h-7 w-7"} ${styles.icon}`}
        strokeWidth={1.8}
      />
    </div>
  );
};

const HomeChallengesSection = ({ theme = "dark" }: { theme?: HomeSectionTheme }) => {
  const themeStyles = HOME_SECTION_THEME_STYLES[theme];

  return (
    <section id="enjeux" className={`${themeStyles.section} home-section`} aria-labelledby="enjeux-title">
      <div className="home-container">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-start lg:gap-8">
          <div className="lg:col-span-5">
            <div
              aria-hidden="true"
              className="mb-3 h-1 w-12 rounded-full"
              style={{ background: "linear-gradient(90deg, #FD41A6, #FF6600)" }}
            />
            <p className={`home-eyebrow mb-2 ${theme === "dark" ? "text-white/70" : "text-gray-500"}`}>
              À QUI S’ADRESSE NOKODIA
            </p>
            <h2 id="enjeux-title" className={`${themeStyles.heading} home-h2 max-w-lg`}>
              Des contextes différents, une même ambition :{" "}
              <span className="text-gradient">faire évoluer la formation</span>.
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-5">
            <p className={`${themeStyles.body} home-section-intro max-w-2xl`}>
              Entreprises, organismes de formation, écoles ou agences : les réalités diffèrent, mais les enjeux se
              rejoignent autour de la transformation, de l’efficacité des dispositifs, de l’évolution des pratiques et
              de la capacité à passer de l’idée à la mise en œuvre.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-5 md:mt-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {CHALLENGES.map(({ target, tagline, description, icon, tone }) => (
            <article key={target} className={`${themeStyles.card} home-card-padding h-full rounded-2xl border`}>
              <div className="flex items-center gap-4">
                <PersonaMotif icon={icon} tone={tone} />
                <h3 className={`${themeStyles.cardHeading} home-h3`}>{target}</h3>
              </div>

              <p className={`${themeStyles.cardHeading} mt-3 text-[15px] font-bold leading-snug`}>{tagline}</p>
              <p className={`${themeStyles.cardBody} home-card-body mt-2`}>{description}</p>
              <a href="#expertises" className="nokodia-link-cta mt-5 text-sm">
                Découvrir les services
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeChallengesSection;
